import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import type { AdminApi } from "@/api";
import { TONE_CLASS } from "@/components/batch-test/batchCells";
import { teName, teSub } from "@/components/batch-test/teSpec";
import { Badge, Banner, Card, CardHead, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { BatchPageDetail, TeBaseline } from "@/types";
import { compactTime } from "@/utils/format";
import { formatScore } from "@/utils/rubric";
import {
  DELTA_EPSILON,
  formatDelta,
  type MatrixColumn,
  type Mover,
  summarizePage,
  type Unscored,
} from "./matrix";
import { tcShort } from "./MatrixTable";
import { BackLink } from "./BackLink";

function errorText(err: unknown) {
  return err instanceof Error ? err.message : String(err);
}

function DeltaText({ delta }: { delta: number | null }) {
  if (delta == null) return <span className="text-muted-foreground">—</span>;
  const tone =
    delta > DELTA_EPSILON ? "text-primary" : delta < -DELTA_EPSILON ? "text-destructive" : "text-muted-foreground";
  return <span className={cn("tabular-nums", tone)}>{formatDelta(delta)}</span>;
}

/**
 * 결과 정리 — one page's results in the questions fe/TEST_CASES_BATCH.md §5
 * asked (fe/TEST_CASES_RESULT.md §6): each TE's mean against the baseline,
 * which TCs moved, and which cells have no score and why.
 *
 * Computed here from the page detail. A page is one set of TCs × a handful
 * of TEs, and every number below is already in its cells; aggregating
 * across pages is Dashboard's (`BE-AGG`), not this screen's.
 */
export function ResultSummary({
  api,
  pageId,
  reloadKey = 0,
}: {
  api: AdminApi;
  pageId: string;
  reloadKey?: number;
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState<BatchPageDetail | null>(null);
  const [baseline, setBaseline] = useState<TeBaseline | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [found, base] = await Promise.all([
          api.getBatchPage(pageId),
          api.getTeBaseline().catch(() => null),
        ]);
        if (!alive) return;
        setPage(found);
        setBaseline(base);
      } catch (err) {
        if (alive) setError(errorText(err));
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, pageId, reloadKey]);

  const summary = useMemo(() => (page ? summarizePage(page) : null), [page]);
  const queries = useMemo(
    () =>
      new Map(
        (page?.test_cases ?? []).map((tc) => [tc.test_case_id, tc.trigger_preview || tc.title || "—"]),
      ),
    [page],
  );

  const back = <BackLink pageId={pageId} label={page?.label ?? null} />;

  if (error) {
    return (
      <div className="grid gap-4">
        {back}
        <Banner tone="error">{error}</Banner>
      </div>
    );
  }
  if (!page || !summary) {
    return (
      <div className="grid gap-4">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
    );
  }

  const open = (column: MatrixColumn, testCaseId: string) =>
    navigate({
      to: "/test-cases/result/runs/$runId/$tcId",
      params: { runId: column.run.batch_run_id, tcId: testCaseId },
    });
  const base = summary.columns.find((c) => c.column.isBaseline)?.column;

  return (
    <div className="grid gap-4">
      {back}

      <Card className="flex flex-wrap items-center gap-4 px-4 py-4">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold tabular-nums">
            {summary.avg != null ? formatScore(summary.avg) : "—"}
          </span>
          <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
            평균 Final · 0~1
          </span>
        </div>
        <div className="grid min-w-0 gap-0.5">
          <div className="text-sm font-semibold">
            {page.label} · {page.name}
          </div>
          <div className="text-xs text-muted-foreground">
            실행 {page.runs.length} 회 · 마지막 {page.runs[0] ? compactTime(page.runs[0].created_at) : "—"} ·{" "}
            {page.created_by_email ?? "—"}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge size="compact">{page.test_case_ids.length} TC 고정</Badge>
          <Badge size="compact">{summary.columns.length} TE</Badge>
          <Badge size="compact">{summary.combos} 조합</Badge>
          <Badge size="compact">채점 {summary.scored}</Badge>
          <Badge size="compact">평가 불가 {summary.notEvaluable}</Badge>
          <Badge size="compact">채점 대기 {summary.notScoredYet}</Badge>
          {/* v1 scores are shown in the Matrix but kept out of every mean. */}
          {summary.commonRubric > 0 && (
            <Badge size="compact" title="TC 와 무관한 공통 기준(0~1) 점수 — rubric v2 평균과 섞지 않습니다">
              common rubric {summary.commonRubric}
            </Badge>
          )}
          {summary.otherRubric > 0 && (
            <Badge size="compact" title="rubric v1 등 다른 눈금의 점수 — 평균·비교에서 뺐습니다">
              다른 rubric · 집계 제외 {summary.otherRubric}
            </Badge>
          )}
          <Badge
            size="compact"
            className={summary.failed > 0 ? "border-destructive-line bg-destructive-surface text-destructive" : ""}
          >
            실패 {summary.failed}
          </Badge>
        </div>
      </Card>

      <Card className="min-w-0 overflow-hidden">
        <CardHead title="TE 별 결과" hint={base ? `기준 ${base.letter} 대비 · 개선·악화는 양쪽 다 채점된 TC 만` : "기준 없음"} />
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-border text-left text-[11px] text-muted-foreground">
                <th className="px-4 py-2 font-normal">TE</th>
                <th className="px-2 py-2 font-normal">구성</th>
                <th className="px-2 py-2 text-right font-normal">평균 Final</th>
                <th className="px-2 py-2 text-right font-normal">Δ 기준</th>
                {/* "Answered worse" or "ran slower" — Final alone cannot say (R3.1). */}
                <th className="px-2 py-2 text-right font-normal" title="평균 Answer Quality">
                  AQ
                </th>
                <th className="px-2 py-2 text-right font-normal" title="평균 Execution">
                  EX
                </th>
                <th className="px-2 py-2 text-right font-normal">개선</th>
                <th className="px-2 py-2 text-right font-normal">악화</th>
                <th className="px-2 py-2 text-right font-normal">동일</th>
                <th className="px-4 py-2 text-right font-normal">채점</th>
              </tr>
            </thead>
            <tbody>
              {summary.columns.map((row) => (
                <tr key={row.column.key} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-2 font-medium">
                    {row.column.letter}
                    {row.column.isBaseline && (
                      <span className="ml-1.5 rounded-full border border-primary/40 px-1.5 text-[10px] text-primary">
                        기준
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {teName(row.column.spec, baseline)}{" "}
                    <span className="text-muted-foreground">{teSub(row.column.spec, baseline)}</span>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {row.stats.avg != null ? formatScore(row.stats.avg) : "—"}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <DeltaText delta={row.delta} />
                  </td>
                  <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">
                    {row.stats.avgAnswerQuality != null ? formatScore(row.stats.avgAnswerQuality) : "—"}
                  </td>
                  <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">
                    {row.stats.avgExecution != null ? formatScore(row.stats.avgExecution) : "—"}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{row.improved ?? "—"}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{row.worsened ?? "—"}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{row.same ?? "—"}</td>
                  <td className="px-4 py-2 text-right text-muted-foreground tabular-nums">
                    {row.stats.scored} / {row.stats.total}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
          도메인별 결과는 도메인 분류(BE-DOMAIN)가 생긴 뒤에 이 아래에 붙습니다.
        </p>
      </Card>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <MoverList title="가장 개선된 조합" movers={summary.improved} queries={queries} onOpen={open} />
        <MoverList title="가장 악화된 조합" movers={summary.worsened} queries={queries} onOpen={open} />
      </div>

      <Card className="min-w-0">
        <CardHead title="점수가 나오지 않은 조합" hint={`${summary.unscored.length} 건`} />
        {summary.unscored.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-muted-foreground">모든 조합이 채점되었습니다.</p>
        ) : (
          <ul>
            {summary.unscored.map((u) => (
              <UnscoredRow key={`${u.column.key}-${u.testCaseId}`} row={u} query={queries.get(u.testCaseId)} onOpen={open} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function MoverList({
  title,
  movers,
  queries,
  onOpen,
}: {
  title: string;
  movers: Mover[];
  queries: Map<string, string>;
  onOpen: (column: MatrixColumn, testCaseId: string) => void;
}) {
  return (
    <Card className="min-w-0">
      <CardHead title={title} hint="기준 대비" />
      {movers.length === 0 ? (
        <p className="px-4 py-6 text-center text-xs text-muted-foreground">해당 없음</p>
      ) : (
        <ul>
          {movers.map((m) => (
            <li key={`${m.column.key}-${m.testCaseId}`} className="border-b border-border last:border-b-0">
              <button
                type="button"
                onClick={() => onOpen(m.column, m.testCaseId)}
                className="grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-4 py-2 text-left text-[13px] hover:bg-muted/60"
              >
                <span className="grid min-w-0">
                  <span className="text-xs text-muted-foreground">
                    {tcShort(m.testCaseId)} × {m.column.letter}
                  </span>
                  <span className="truncate">{queries.get(m.testCaseId) ?? "—"}</span>
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatScore(m.from)} → {formatScore(m.to)}
                </span>
                <DeltaText delta={m.delta} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function UnscoredRow({
  row,
  query,
  onOpen,
}: {
  row: Unscored;
  query: string | undefined;
  onOpen: (column: MatrixColumn, testCaseId: string) => void;
}) {
  const tone = row.label === "실패" ? TONE_CLASS.failed : TONE_CLASS.na;
  return (
    <li className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => onOpen(row.column, row.testCaseId)}
        className="grid w-full grid-cols-[72px_minmax(0,1fr)] items-center gap-3 px-4 py-2 text-left text-[13px] hover:bg-muted/60"
      >
        <span className={cn("rounded-md py-0.5 text-center text-[11px]", tone)}>{row.label}</span>
        <span className="grid min-w-0">
          <span className="text-xs text-muted-foreground">
            {tcShort(row.testCaseId)} × {row.column.letter}
            {row.reason ? ` · ${row.reason}` : ""}
          </span>
          <span className="truncate">{query ?? "—"}</span>
        </span>
      </button>
    </li>
  );
}
